import { Component, Emit, Ref, Vue } from 'vue-property-decorator'
import ValuableConceptsModalComponent from '@/components/valuable-concepts-modal/valuable-concepts-modal.vue'
import ValuableConceptsModal from '@/components/valuable-concepts-modal/valuable-concepts-modal'
import BillingModalDeliveryNotesCrudComponent from '@/modules/billing-module/components/modal/delivery-notes/crud/billing-modal-delivery-notes-crud.vue'
import BillingModalDeliveryNotesCrud from '@/modules/billing-module/components/modal/delivery-notes/crud/billing-modal-delivery-notes-crud'
import BillingModalDeliveryNotesAddToComponent from '@/modules/billing-module/components/modal/delivery-notes/add-to/billing-modal-delivery-notes-add-to.vue'
import BillingModalDeliveryNotesAddTo from '@/modules/billing-module/components/modal/delivery-notes/add-to/billing-modal-delivery-notes-add-to'
import {
    AppliedBillableConceptResponse,
    BillingCopaymentType
} from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { BillingStoreHelper } from '@/modules/billing-module/store/billing-store.helper'
import { MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { StrictNullable } from '@/shared/types/app.types'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { billingCommonDeliveryNoteService } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.service'
import { store } from '@/store/store'
import { HelperUtils } from '@/shared/util/helper-utils'
import { BillingOperation } from '@/api/operations/models/billing-operation'

@Component({
    components: {
        ValuableConceptsModalComponent,
        BillingModalDeliveryNotesAddToComponent,
        BillingModalDeliveryNotesCrudComponent
    }
})
export default class BillingGridActionsCopayments extends Vue {
    @Ref() readonly conceptsModal!: ValuableConceptsModal
    @Ref() readonly deliveryNotesModal!: BillingModalDeliveryNotesCrud
    @Ref() readonly deliveryNotesAddToModal!: BillingModalDeliveryNotesAddTo

    @Emit()
    refresh(): void {
        // emits an event
    }

    private static get _selectedRows(): AppliedBillableConceptResponse[] {
        return BillingStoreHelper.masterSelectedRows as AppliedBillableConceptResponse[]
    }
    private static get _selectedRow(): StrictNullable<AppliedBillableConceptResponse> {
        return BillingGridActionsCopayments._selectedRows.length === 1
            ? BillingGridActionsCopayments._selectedRows[0]
            : null
    }

    get canUpdateConcept(): boolean {
        return BillingGridActionsCopayments._selectedRows.length === 1 &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_APPLIED_CONCEPTS)
    }
    get canCreateDeliveryNote(): boolean {
        const hasAnySelectedRow =
            !!BillingGridActionsCopayments._selectedRows.length
        const sameAccountingUnit =
            BillingGridActionsCopayments._selectedRows.every(selectedRow =>
                selectedRow.rate?.accountingUnit.id === BillingGridActionsCopayments._selectedRows[0].rate?.accountingUnit.id)
        const sameIsForAutoInvoice =
            BillingGridActionsCopayments._selectedRows.every(selectedRow =>
                selectedRow.isForAutoInvoice === BillingGridActionsCopayments._selectedRows[0].isForAutoInvoice)
        const sameCustomerOrPatient =
            BillingGridActionsCopayments._selectedRows.every(selectedRow =>
                (!!selectedRow.customer && selectedRow.customer.id === BillingGridActionsCopayments._selectedRows[0].customer?.id) ||
                (!selectedRow.customer && selectedRow.caseStudy.patient.id === BillingGridActionsCopayments._selectedRows[0].caseStudy.patient.id))
        const sameCopaymentPatientType =
            BillingGridActionsCopayments._selectedRows.every(selectedRow =>
                !!selectedRow.copaymentType && selectedRow.copaymentType.code === BillingCopaymentType.PATIENT)

        /// breakdowns
        const customerHasBreakdownByCaseType =
            BillingGridActionsCopayments._selectedRows.every(selectedRow =>
                !!selectedRow.customer &&
                selectedRow.customer.breakdownDeliveryNotesByCaseType &&
                selectedRow.caseStudy.caseType.id !== BillingGridActionsCopayments._selectedRows[0].caseStudy.caseType.id)
        const customerHasBreakdownByPatient =
            BillingGridActionsCopayments._selectedRows.every(selectedRow =>
                !!selectedRow.customer &&
                selectedRow.customer.breakdownDeliveryNotesByPatient &&
                selectedRow.caseStudy.patient.id !== BillingGridActionsCopayments._selectedRows[0].caseStudy.patient.id)

        return hasAnySelectedRow &&
            sameAccountingUnit &&
            sameIsForAutoInvoice &&
            sameCustomerOrPatient &&
            sameCopaymentPatientType &&
            !customerHasBreakdownByCaseType &&
            !customerHasBreakdownByPatient &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_DELIVERY_NOTES)
    }
    get canAddToDeliveryNote(): boolean {
        return this.canCreateDeliveryNote
    }

    /// EVENT HANDLERS ///

    onUpdateConcept(): void {
        if (BillingGridActionsCopayments._selectedRow) {
            this.conceptsModal.show({
                isBillingModule: true,
                caseId: BillingGridActionsCopayments._selectedRow.caseStudy.id,
                accountingId: BillingGridActionsCopayments._selectedRow.unit?.id,
                entityConcept: {
                    entityId: BillingGridActionsCopayments._selectedRow.entityId,
                    entityType: BillingGridActionsCopayments._selectedRow.entityType.code
                }
            })
        }
    }

    onCreateDeliveryNote(): void {
        this.deliveryNotesModal.show({
            appliedBillableConcepts: BillingGridActionsCopayments._selectedRows
        })
    }

    async onAddToDeliveryNote(): Promise<void> {
        await this.deliveryNotesAddToModal.show({
            appliedBillableConcepts: BillingGridActionsCopayments._selectedRows
        })
    }
    async onAddedToDeliveryNote(deliveryNoteId: number): Promise<void> {
        this.deliveryNotesAddToModal.hide()
        this.refresh()

        const deliveryNote =
            await PromiseUtils.toCallSecure(
                billingCommonDeliveryNoteService.get(store.state.labId, deliveryNoteId))
        if (deliveryNote !== null) {
            this.deliveryNotesModal.show({ deliveryNote })
        }
    }

    ///

    getContextMenuItems(): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (this.canUpdateConcept) {
            menuItems.push({
                name: this.$tc('accounting.action.updateConcept'),
                action: this.onUpdateConcept
            })
        }
        if (this.canCreateDeliveryNote) {
            menuItems.push({
                name: this.$tc('accounting.action.generateDeliveryNote'),
                action: this.onCreateDeliveryNote
            })
        }
        if (this.canAddToDeliveryNote) {
            menuItems.push({
                name: this.$tc('accounting.action.addToDeliveryNote'),
                action: this.onAddToDeliveryNote
            })
        }
        return menuItems
    }
}
